<script setup>
import { ref, defineExpose } from 'vue';
import { useCommonData } from '@/hooks';
import { useQuarterlyCommon } from './useQuarterlyCommon';
import { convertNumberToCNUnit } from '@/utils/api.js';
import { useModuleNavigation } from '@rebuild/hooks/useModuleNavigation';

const { core_option, percentCore } = useQuarterlyCommon();
const { getOrganizationName, getStockInfoVOS } = useCommonData();

const OrganizationName = ref(null);
const code = ref(null);
OrganizationName.value = getOrganizationName.value;
code.value = getStockInfoVOS.value?.[0].code;

const handleCoreUnit = (val, key) => {
	const { unit } = convertNumberToCNUnit(val);
	return unit ? `(${unit})` : '';
};

const handleCoreValue = (val, key) => {
	return percentCore.includes(key) ? val.toFixed(2) + '%' : convertNumberToCNUnit(val)?.value;
};

const { viewModule } = useModuleNavigation();
const itemHandler = (type, item) => {
	const { announcementLink, roundupLink, showLink, roadshowLink, id, longImage } = item;
	if (type === 'announcementLink') {
		viewModule('quarterly_performance_announcement', { comeinLink: announcementLink, id });
	} else if (type === 'roundupLink') {
		viewModule('quarterly_performance_summary', { comeinLink: roundupLink, id });
	} else if (type === 'showLink') {
		viewModule('quarterly_performance_custom', { comeinLink: showLink, id });
	} else if (type === 'roadshowLink') {
		viewModule('quarterly_performance_roadshow', { roadshowLink, id });
	} else if (type === 'imageLink') {
		viewModule('quarterly_performance_image_preview', { url: longImage });
	}
};

const coreRefresh = result => {
	initData(result);
	return { empty: lastQuarterData.value.length === 0 };
};

const lastQuarterData = ref([]);
const initData = data => {
	let arr = Object.values(data) || [];
	arr.forEach(item => {
		item.sort((a, b) => a.quarter - b.quarter);
	});

	lastQuarterData.value = arr?.flat()?.slice(-3)?.reverse();
};

defineExpose({
	coreRefresh
});
</script>

<template>
	<div class="quarter-wrapper--v2">
		<div class="quarter-box" v-for="(item, index) in lastQuarterData" :key="index">
			<SvgIcons class="Q" :name="`Q${item.quarter}`" />
			<div class="quarter-box-left">
				<h3 class="text-overflow">
					<span
						>{{ OrganizationName }}<span class="code">（{{ code }}）</span></span
					>{{ item.announcementTitle }}
				</h3>
				<p>
					<!-- 业绩公告 -->
					<span class="performance-item" @click="itemHandler('announcementLink', item)"
						><SvgIcons class="performance-svg" name="quarter-icon1" />{{
							$t('enterprise.results-announcement')
						}}</span
					>
					<!--演示材料  -->
					<span class="performance-item" v-if="item.showTitle" @click="itemHandler('showLink', item)"
						><SvgIcons class="performance-svg" name="quarter-icon2" />{{
							$t('enterprise.earnings-library')
						}}</span
					>
					<!-- 业绩路演 -->
					<span class="performance-item" v-if="item.roadshowId" @click="itemHandler('roadshowLink', item)"
						><SvgIcons class="performance-svg" name="quarter-icon3" />{{
							$t('enterprise.earnings-calls')
						}}</span
					>
					<!-- 业绩摘要 -->
					<span class="performance-item" v-if="item.roundupTitle" @click="itemHandler('roundupLink', item)"
						><SvgIcons class="performance-svg" name="quarter-icon4" />{{
							$t('enterprise.results-summary')
						}}</span
					>
					<!-- 一图读懂 -->
					<span class="performance-item" v-if="item.longImage" @click="itemHandler('imageLink', item)"
						><SvgIcons class="performance-svg" name="quarter-icon5" />{{
							$t('enterprise.page-version')
						}}</span
					>
				</p>
			</div>
			<div class="quarter-box-right">
				<div class="performance-index" v-for="(value, key) in item.coreIndicators" :key="key">
					<h4>
						{{ handleCoreValue(value, key) }}
					</h4>
					<p>{{ core_option[key] }}{{ handleCoreUnit(value) }}</p>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="less" scoped>
.quarter-box {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px;

	border: 0.5px solid var(--border-lighter-color, #f2f3f5);
	background: linear-gradient(270deg, var(--theme-color-gradient-000) 0%, var(--theme-color-gradient-003) 100%);
	&:not(:last-child) {
		margin-bottom: 20px;
	}
	.Q {
		position: absolute;
		top: -12px;
		left: 45%;
		width: 90px;
		height: 80px;
		color: var(--theme-color-gradient-005) !important;
	}
	.quarter-box-left {
		max-width: 50%;
		h3 {
			width: 100%;
			color: var(--t-emphasis-color, #1d2129);
			font-size: 20px;
			font-weight: 500;
			line-height: 28px; /* 140% */
		}
		p {
			padding-top: 8px;
			display: flex;
			justify-content: flex-start;
			align-items: center;
			.performance-item {
				flex-shrink: 0;
				padding-right: 16px;
				display: flex;
				color: var(--t-secondary-color, #4e5969);
				font-size: 14px;
				font-weight: 400;
				line-height: 18px; /* 128.571% */
				cursor: pointer;
				&:not(:last-child) {
					border-right: 1px solid #f2f3f5;
				}
				&:not(:first-child) {
					padding-left: 16px;
				}
				.performance-svg {
					padding-right: 4px;
					width: 20px;
					height: 20px;
				}
			}
		}
	}

	.quarter-box-right {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-gap: 30px;
		width: 35%;
		flex-shrink: 0;
		.performance-index {
			text-align: left;
			h4 {
				padding-bottom: 8px;
				color: var(--t-emphasis-color, #1d2129);
				font-family: D-DIN;
				font-size: 20px;
				font-weight: 700;
				line-height: 20px; /* 100% */
				.core-on-year {
					padding-left: 8px;
					color: var(---, #f23d3d);
					font-family: D-DIN;
					font-size: 12px;
					font-weight: 700;
					line-height: 20px;
				}
			}
			p {
				color: var(--t-secondary-info-color, #86909c);
				font-size: 14px;
				font-weight: 400;
				line-height: 18px; /* 128.571% */
			}
		}
	}
}
</style>
